import React, { useState, useEffect, useRef } from "react";
import axios from "../common/axios";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "../components/ui/card";
import ShadcnPageWrapper from "./ShadcnPageWrapper";

export default function SignIn() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const navigate = useNavigate(); // useNavigate 사용

  const ssoPopupRef = useRef(null);
  const openKnoxSSO = () => {
    const width = 500,
      height = 600;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;
    ssoPopupRef.current = window.open(
      "/sso-popup",
      "knox-sso",
      `width=${width},height=${height},left=${left},top=${top},resizable=no,scrollbars=no`
    );
  };

  useEffect(() => {
    const handleMessage = async (event) => {
      if (event.origin !== window.location.origin) return;
      const { type, email, error } = event.data;
      if (type === "SSO_SUCCESS") {
        Swal.fire({
          title: "로그인 성공!",
          text: "Knox SSO 로그인이 완료되었습니다.",
          icon: "success",
          confirmButtonText: "OK",
        }).then(() => navigate("/home"));
      } else if (type === "SSO_NO_USER") {
        Swal.fire({
          title: "미등록 사용자",
          text: "등록된 계정이 없습니다. 회원가입 페이지로 이동합니다.",
          icon: "warning",
          confirmButtonText: "OK",
        }).then(() =>
          navigate("/dpsignup" + (email ? `?email=${encodeURIComponent(email)}` : ""))
        );
      } else if (type === "SSO_ERROR") {
        Swal.fire({
          title: "오류",
          text: error || "Knox SSO 인증에 실패했습니다.",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const loginResponse = await axios.post(
        `/dplogin`,
        { username, password },
        { withCredentials: true }
      );

      if (loginResponse.data.success) {
        // Fetch user data after successful login

        Swal.fire({
          title: "로그인 성공!",
          text: loginResponse.data.message,
          icon: "success",
          confirmButtonText: "OK",
        }).then(() => {
          navigate("/home");
        });
      } else {
        Swal.fire({
          title: "문제가 있습니다!",
          text: loginResponse.data.message,
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.error || "로그인 중 문제가 발생했습니다.";

      // 비밀번호 만료 오류에 대한 처리 추가
      if (err.response?.data?.passwordExpired) {
        Swal.fire({
          title: "비밀번호 만료!",
          text: "비밀번호가 만료되었습니다. 비밀번호를 재설정해주세요.",
          icon: "warning",
          confirmButtonText: "비밀번호 재설정",
        }).then((result) => {
          if (result.isConfirmed) {
            // 사용자가 확인 버튼을 클릭하면 비밀번호 재설정 페이지로 리다이렉션
            navigate("/dpreset-password");
          }
        });
      } else {
        // 다른 종류의 오류에 대한 SweetAlert 표시
        Swal.fire({
          title: "오류!",
          text: errorMessage,
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    }
  };

  return (
    <ShadcnPageWrapper>
      <div className="flex min-h-screen">
        {/* 좌측: 로그인 폼 */}
        <div className="flex flex-1 flex-col items-center justify-center px-4 py-12">
          <div className="w-full sm:w-[350px]">
            <div className="mb-8 flex flex-col items-center gap-2">
              <img src="/images/g1313.png" alt="삼성중공업" className="h-10 w-auto" />
              <h1 className="m-0 text-xl font-bold text-foreground">
                삼성중공업 하이테크 통합 DP 시스템
              </h1>
              <p className="m-0 text-sm text-muted-foreground">계정에 로그인하세요</p>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="text-base no-underline text-card-foreground">로그인</CardTitle>
                <CardDescription>아이디와 비밀번호를 입력하세요.</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="username">아이디</Label>
                    <Input
                      id="username"
                      type="text"
                      name="username"
                      placeholder="아이디"
                      autoComplete="off"
                      onChange={(e) => setUsername(e.target.value)}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="password">비밀번호</Label>
                    <Input
                      id="password"
                      type="password"
                      name="password"
                      placeholder="비밀번호"
                      autoComplete="off"
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  </div>

                  <div className="text-right -mt-1">
                    <RouterLink
                      to={{ pathname: "/dpreset-password" }}
                      className="text-xs text-muted-foreground hover:text-foreground hover:underline"
                    >
                      비밀번호를 잊으셨나요?
                    </RouterLink>
                  </div>

                  <Button
                    type="submit"
                    className="w-full border-0 bg-[hsl(222,80%,40%)] text-[#fff] hover:bg-[hsl(222,80%,40%)]/80"
                  >
                    로그인
                  </Button>
                </form>

                {/* Knox SSO 로그인: login.js와 동일하게 실제 동작은 비활성 상태 유지 */}
                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">또는</span>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={openKnoxSSO}
                  disabled
                >
                  Knox SSO 로그인
                </Button>

                <div className="mt-4 space-y-2">
                  <p className="m-0 text-center text-sm text-muted-foreground">
                    아직 삼성중공업 하이테크의 DP 회원이 아니신가요?
                  </p>
                  <div className="flex gap-2">
                    <RouterLink
                      to={{ pathname: "/dpsignup" }}
                      className="flex-1 rounded-md border border-input bg-background py-2 px-3 text-center text-xs font-medium text-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                    >
                      회원가입
                    </RouterLink>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* 우측: 브랜드 패널 (lg 이상에서만 표시) */}
        <div className="hidden flex-1 flex-col items-center justify-center bg-[#0F2744] px-12 lg:flex">
          <div className="max-w-md text-center text-[#fff]">
            <h2 className="m-0 text-3xl font-bold leading-tight text-[#fff]">
              삼성중공업 하이테크 부문 DP 시스템
            </h2>
            <p className="m-0 mt-4 text-base text-[#fff]/70">
              Oracle Unifier와 연동되어 승인도·제작도의 계획/실적 데이터를 실시간으로
              관리합니다. 등록된 계정으로 로그인해 주세요.
            </p>
          </div>
        </div>
      </div>
    </ShadcnPageWrapper>
  );
}
